import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { SECTION_ROUTES, NAV_SECTIONS } from '../config/routes';
import type { NavSectionKey } from '../config/routes';
import NavIcon from './NavIcons';
import { useOverlay } from './ui/useOverlay';
import './UpgradeModal.css';

/**
 * Global upgrade prompt rendered as a modal popup rather than a full page.
 *
 * Any component can open it via `useUpgradeModal()`:
 *   const { openFeature, openSection } = useUpgradeModal();
 *   openFeature('strategy');          // single premium feature
 *   openSection('emissions');         // whole fully-locked navbar section
 *
 * The `/upgrade/:feature` and `/upgrade-section/:section` routes remain as
 * deep-link fallbacks (shared emails / bookmarks), but every in-app click now
 * opens this popup instead of navigating away.
 */

type UpgradeTarget =
  | { kind: 'feature'; key: string }
  | { kind: 'section'; key: NavSectionKey }
  | { kind: 'custom'; title: string; message: string };

interface UpgradeModalContextValue {
  openFeature: (featureKey: string) => void;
  openSection: (sectionKey: NavSectionKey) => void;
  /** Ad-hoc prompt for a locked surface that isn't a whole navbar feature or
   *  section (e.g. the Scope 3 block of the GHG inventory page). */
  openCustom: (title: string, message: string) => void;
  close: () => void;
}

const UpgradeModalContext = createContext<UpgradeModalContextValue | null>(null);

export const useUpgradeModal = (): UpgradeModalContextValue => {
  const ctx = useContext(UpgradeModalContext);
  if (!ctx) {
    throw new Error('useUpgradeModal must be used within an UpgradeModalProvider');
  }
  return ctx;
};

const MAILTO = 'mailto:hello@econori.com?subject=';

const pillStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 10,
  padding: '6px 12px',
  borderRadius: 999,
  background: 'rgba(73, 43, 98, 0.08)',
  color: 'var(--brand-primary)',
  fontFamily: 'Montserrat, sans-serif',
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  marginBottom: 16,
};

const Pill: React.FC<{ label: string }> = ({ label }) => (
  <span style={pillStyle}>
    <NavIcon name="lock" size={14} />
    {label}
  </span>
);

const ctaStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  padding: '0.7rem 1.25rem',
  background: 'var(--brand-primary)',
  color: '#ffffff',
  borderRadius: 6,
  textDecoration: 'none',
  fontFamily: 'Montserrat, sans-serif',
  fontWeight: 600,
  fontSize: '0.95rem',
};

const FeatureBody: React.FC<{ featureKey: string }> = ({ featureKey }) => {
  const route = SECTION_ROUTES.find((r) => r.featureKey === featureKey);
  return (
    <>
      <Pill label="Upgrade required" />
      <h2 id="upgrade-modal-title" className="upgrade-modal-heading">
        {route?.label ?? 'This feature'}
      </h2>
      {route?.tagline && <p className="upgrade-modal-tagline">{route.tagline}</p>}
      <p className="upgrade-modal-copy">
        {route
          ? `${route.label} is a premium feature and isn't included in your current plan. ` +
            'Get in touch to add it to your subscription, or talk to us about the package ' +
            'that best fits your business.'
          : "This feature isn't included in your current plan. Get in touch to upgrade."}
      </p>
      <a style={ctaStyle} href={`${MAILTO}${encodeURIComponent('Upgrade enquiry')}`}>
        Talk to us about upgrading
      </a>
    </>
  );
};

const CustomBody: React.FC<{ title: string; message: string }> = ({
  title,
  message,
}) => (
  <>
    <Pill label="Enterprise feature" />
    <h2 id="upgrade-modal-title" className="upgrade-modal-heading">
      {title}
    </h2>
    <p className="upgrade-modal-copy">{message}</p>
    <a style={ctaStyle} href={`${MAILTO}${encodeURIComponent(`Upgrade enquiry — ${title}`)}`}>
      Talk to us about upgrading
    </a>
  </>
);

const SectionBody: React.FC<{ sectionKey: NavSectionKey }> = ({ sectionKey }) => {
  const sectionConfig = NAV_SECTIONS.find((s) => s.key === sectionKey);
  const features = SECTION_ROUTES.filter((r) => r.section === sectionKey);

  if (!sectionConfig || features.length === 0) {
    return (
      <>
        <h2 id="upgrade-modal-title" className="upgrade-modal-heading">
          Section not found
        </h2>
        <p className="upgrade-modal-copy">
          This upgrade prompt doesn&apos;t correspond to a known section.
        </p>
      </>
    );
  }

  const subject = `Upgrade enquiry — ${sectionConfig.label}`;
  return (
    <>
      <Pill label="Premium section" />
      <h2 id="upgrade-modal-title" className="upgrade-modal-heading">
        {sectionConfig.label}
      </h2>
      <p className="upgrade-modal-tagline">
        Unlock the full {sectionConfig.label.toLowerCase()} suite — {features.length}{' '}
        {features.length === 1 ? 'feature' : 'features'} that turn your sustainability
        data into action and proof.
      </p>
      <div className="upgrade-modal-feature-grid">
        {features.map((feature) => (
          <div key={feature.featureKey} className="upgrade-modal-feature-card">
            <span className="upgrade-modal-feature-icon">
              <NavIcon name={feature.icon} size={18} />
            </span>
            <span className="upgrade-modal-feature-label">{feature.label}</span>
            <span className="upgrade-modal-feature-tagline">{feature.tagline}</span>
          </div>
        ))}
      </div>
      <a style={ctaStyle} href={`${MAILTO}${encodeURIComponent(subject)}`}>
        Talk to us about upgrading
      </a>
    </>
  );
};

export const UpgradeModalProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [target, setTarget] = useState<UpgradeTarget | null>(null);

  const close = useCallback(() => setTarget(null), []);
  const openFeature = useCallback(
    (key: string) => setTarget({ kind: 'feature', key }),
    [],
  );
  const openSection = useCallback(
    (key: NavSectionKey) => setTarget({ kind: 'section', key }),
    [],
  );
  const openCustom = useCallback(
    (title: string, message: string) =>
      setTarget({ kind: 'custom', title, message }),
    [],
  );

  // Not the shared Modal: the pill above the heading and the accent edge are the
  // design. The hook still gives it Esc and a place on the overlay stack, so Esc
  // over a Modal that opened this prompt (the connection request does) closes
  // only the prompt.
  const dialogRef = useOverlay<HTMLDivElement>(Boolean(target), close);

  const value = useMemo(
    () => ({ openFeature, openSection, openCustom, close }),
    [openFeature, openSection, openCustom, close],
  );

  return (
    <UpgradeModalContext.Provider value={value}>
      {children}
      {target &&
        createPortal(
          <div
            className="upgrade-modal-overlay"
            onClick={close}
            data-testid="upgrade-modal-overlay"
          >
            <div
              ref={dialogRef}
              className="upgrade-modal-dialog"
              role="dialog"
              aria-modal="true"
              aria-labelledby="upgrade-modal-title"
              tabIndex={-1}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                className="upgrade-modal-close"
                onClick={close}
                aria-label="Close"
              >
                <NavIcon name="close" size={18} />
              </button>
              {target.kind === 'feature' && (
                <FeatureBody featureKey={target.key} />
              )}
              {target.kind === 'section' && (
                <SectionBody sectionKey={target.key} />
              )}
              {target.kind === 'custom' && (
                <CustomBody title={target.title} message={target.message} />
              )}
            </div>
          </div>,
          document.body,
        )}
    </UpgradeModalContext.Provider>
  );
};

export default UpgradeModalProvider;
